import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import FavoriteButton from '@/components/feature/FavoriteButton';
import ShareModal from '@/components/feature/ShareModal';
import SectionHeading from '@/components/feature/SectionHeading';
import HotRanking from '@/pages/home/components/HotRanking';
import { getArticleById, getHot, getRelated, formatDate, formatViews } from '@/utils/articles';
import { getCategoryMeta } from '@/mocks/categories';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function DetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const article = getArticleById(id);
  const [shareOpen, setShareOpen] = useState(false);

  useDocumentMeta({
    title: article ? `${article.title} - 91吃瓜` : '内容不存在 - 91吃瓜',
    description: article?.summary,
    keywords: article ? `${article.tags.join(',')},91吃瓜` : '91吃瓜',
  });

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading font-black text-2xl text-foreground-950">404 · 内容不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">这篇文章可能已被移除，或链接有误</p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
          <Link
            to="/category/news"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 border border-background-200 text-sm font-medium hover:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            浏览娱乐新闻
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryMeta(article.category);
  const related = getRelated(article, 4);
  const hot = getHot(5);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={category?.path ?? '/'} className="hover:text-primary-600 transition-colors cursor-pointer">
          {category?.name}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800 line-clamp-1">{article.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <article className="lg:col-span-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={category?.path ?? '/'}
              className="px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              {category?.name}
            </Link>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded text-xs bg-secondary-100 text-secondary-800 whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl leading-tight text-foreground-950">
            {article.title}
          </h1>

          <div className="mt-4 flex items-center gap-4 flex-wrap text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {formatDate(article.publishTime)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(article.views)} 阅读
            </span>
          </div>

          <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 91吃瓜`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8">
            {article.paragraphs.map((paragraph, i) => (
              <div key={`p-${i}`}>
                <p
                  className={`text-foreground-700 leading-[1.9] ${
                    i === 0 ? 'text-base md:text-lg font-medium text-foreground-900' : 'mt-5 text-base'
                  }`}
                >
                  {paragraph}
                </p>
                {i === 1 && article.gallery[0] && (
                  <figure className="mt-6">
                    <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
                      <img
                        src={article.gallery[0]}
                        alt={`${article.title} 配图`}
                        title={`${article.title} - 91吃瓜`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <figcaption className="mt-2 text-xs text-foreground-400 text-center">
                      资料配图 · 91吃瓜
                    </figcaption>
                  </figure>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200">
            <div className="flex items-center gap-3 flex-wrap">
              <FavoriteButton id={article.id} showLabel />
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-50 text-foreground-800 border border-background-200 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line text-base" />
                分享文章
              </button>
              <span className="text-xs text-foreground-400 ml-auto hidden sm:block">
                收藏仅保存在当前设备，不会上传
              </span>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-10">
              <SectionHeading
                title="相关推荐"
                subtitle="基于分类与关键词为你推荐"
                icon="ri-links-line"
                moreLabel={`更多${category?.name ?? ''}`}
                moreTo={category?.path ?? '/category/news'}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          )}
        </article>

        <aside className="flex flex-col gap-8">
          <div>
            <SectionHeading title="热门内容榜" icon="ri-fire-line" />
            <HotRanking articles={hot} />
          </div>
          <div className="rounded-lg bg-background-50 border border-background-200 p-5">
            <h3 className="font-heading font-bold text-base text-foreground-950">浏览更多频道</h3>
            <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
              想找更多同类内容？进入分类页可以按标签和热度继续筛选。
            </p>
            <Link
              to={category?.path ?? '/category/news'}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              进入{category?.name}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </div>
  );
}